import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Image as ImageIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events?is_active=true`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mock events for demo (will be replaced with real data from backend)
  const mockEvents = [
    {
      id: '1',
      title: 'Industrial Trade Show 2024',
      description: 'Showcasing our latest cutting tool innovations and meeting industry leaders.',
      event_date: '2024-03-15',
      location: 'India Expo Centre, Greater Noida',
      images: [
        { url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=800', caption: 'Exhibition Booth' },
        { url: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?q=80&w=800', caption: 'Product Display' }
      ]
    },
    {
      id: '2',
      title: 'Technical Workshop on CNC Machining',
      description: 'Conducted hands-on training session for our clients on advanced machining techniques.',
      event_date: '2024-02-20',
      location: 'Namoh Toolings Training Center, Ludhiana',
      images: [
        { url: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=800', caption: 'Workshop Session' }
      ]
    },
    {
      id: '3',
      title: 'Customer Appreciation Day',
      description: 'Celebrating partnerships with our valued customers and showcasing new product launches.',
      event_date: '2024-01-10',
      location: 'Namoh Toolings Head Office',
      images: [
        { url: 'https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=800', caption: 'Event Gathering' }
      ]
    }
  ];

  const displayEvents = events.length > 0 ? events : mockEvents;

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white">
      {/* Header */}
      <section className="bg-[#2E3A6F] text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2000')"
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2E3A6F] via-[#2E3A6F]/95 to-[#2E3A6F]/90" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="max-w-3xl mx-auto text-center space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4">
              <Calendar size={32} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold">Our Events</h1>
            <p className="text-lg text-white/90">
              Stay updated with our latest exhibitions, workshops, and industry events
            </p>
          </motion.div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading events...</p>
            </div>
          ) : displayEvents.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 text-lg">No events available at the moment.</p>
            </div>
          ) : (
            <motion.div 
              className="space-y-12"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              {displayEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  variants={fadeInUp}
                >
                  <Card className="border-0 bg-white hover:shadow-2xl transition-all duration-500 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                      {/* Images */}
                      <div className="relative h-80 lg:h-auto">
                        {event.images && event.images.length > 0 ? (
                          <div className="grid grid-cols-2 gap-2 h-full p-4">
                            {event.images.slice(0, 4).map((img, imgIndex) => (
                              <motion.div
                                key={imgIndex}
                                className={`relative overflow-hidden rounded-lg ${
                                  event.images.length === 1 ? 'col-span-2' : ''
                                }`}
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.3 }}
                              >
                                <img
                                  src={img.url}
                                  alt={img.caption || event.title}
                                  className="w-full h-full object-cover"
                                />
                                {img.caption && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2">
                                    {img.caption}
                                  </div>
                                )}
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full bg-gray-100">
                            <ImageIcon className="text-gray-400" size={64} />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <CardContent className="p-8 flex flex-col justify-center">
                        <div className="space-y-4">
                          <h2 className="text-3xl font-bold text-gray-900">{event.title}</h2>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            {event.event_date && (
                              <div className="flex items-center gap-2">
                                <Calendar size={16} className="text-[#2E3A6F]" />
                                <span>{new Date(event.event_date).toLocaleDateString('en-IN', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })}</span>
                              </div>
                            )}
                            {event.location && (
                              <div className="flex items-center gap-2">
                                <MapPin size={16} className="text-[#2E3A6F]" />
                                <span>{event.location}</span>
                              </div>
                            )}
                          </div>

                          {event.description && (
                            <p className="text-gray-700 leading-relaxed">
                              {event.description}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white border-t">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">Want to Participate in Our Events?</h2>
            <p className="text-lg text-gray-600">
              Stay connected with us for upcoming exhibitions, workshops, and training sessions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/enquiry">
                <motion.button
                  className="px-8 py-3 bg-[#2E3A6F] text-white rounded-lg font-medium hover:bg-[#1f2747] transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Contact Us
                </motion.button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
